<script setup>
import { computed, ref } from 'vue'
import { useLoadCommentsStore } from '@/stores/LoadComments'
import { useToastStore } from '@/stores/Toast'

/**
 * The component's props.
 */
const props = defineProps({
    /**
     * The load id.
     *
     * @type {Number}
     */
    loadId: {
        type: Number,
        required: true,
    },
})

/**
 * The store instances.
 */
const store = useLoadCommentsStore()
const toastStore = useToastStore()

/**
 * The local refs.
 */
const priority = ref(0)
const content = ref(null)
const isLoading = ref(false)

/**
 * Check if the content has errors.
 *
 * @returns {Boolean}
 */
const hasErrors = computed(() => {
    return content.value === null || !content.value?.trim().length
})

/**
 * Handle the save action.
 *
 * @return {void}
 */
const onSave = async () => {
    try {
        isLoading.value = true

        await store.create({
            loadId: props.loadId,
            comment: content.value,
            priority: priority.value,
        })

        toastStore.setNotification({
            message: `Comment has been added.`,
            type: 'success',
        })

        content.value = null
    } catch (error) {
        toastStore.setNotification({
            message: `Comment could not be added.`,
            type: 'error',
        })
    } finally {
        isLoading.value = false
    }
}
</script>

<template>
    <div class="flex flex-col px-6 mt-auto">
        <v-textarea
            label="Comment"
            variant="outlined"
            rows="2"
            v-model="content"
            density="compact"
        ></v-textarea>
        <div class="flex justify-between items-center">
            <v-radio-group label="Priority" v-model="priority" inline>
                <v-radio checked label="Low" :value="0" color="green"></v-radio>
                <v-radio label="Normal" color="blue" :value="1"></v-radio>
                <v-radio label="High" color="red" :value="2"></v-radio>
            </v-radio-group>
            <v-btn
                data-test="panel-save-btn"
                color="primary"
                :loading="isLoading"
                :disabled="hasErrors"
                @click.prevent="onSave"
            >
                Save
            </v-btn>
        </div>
    </div>
</template>
